import React, { useCallback, useEffect, useRef, useState } from "react";

import GoogleMapReact from "google-map-react";

import { checkExperienceCookieConsent, getGoogleKey, useCookieConsent, useGetFieldShapes, useNavigate, useTranslation } from "utils";

import { ALL_FIELDS_ROUTE, COUNTRY_CODE, EARTH_RADIUS_M, GOOGLE_MAPS_TILE_SIZE_PX, MAP_LAYER_MAX_ZOOM, MAP_PLACEHOLDER_IMAGE } from "consts";

import { Coordinates, FarmResponse, FieldListField } from "models";

import { Icon, LazyLoadImage, MapCookieConsentReminder, Skeleton } from "components";

import type { FieldStatus } from "pages/app/all-field-view/hooks/useFieldStatusMap";
import { useMapPolygons } from "pages/app/all-field-view/hooks/useMapPolygons";
import { FarmMapEmptyBar, FarmMapUnavailableBar } from "pages/app/home/farm-map/FarmMapEmptyBar";
import { FarmMapStatsBar } from "pages/app/home/farm-map/FarmMapStatsBar";
import { FarmMapZoomControl } from "pages/app/home/farm-map/FarmMapZoomControl";
import { countryCentre, useFarmLocation } from "pages/app/home/farm-map/useFarmLocation";
import { HomeFieldStats } from "pages/app/home/status-cards/homeFieldStats";

export type FarmMapPanelProps = {
  farm: FarmResponse;
  city: string | null;
  /** the farm details with the city are still loading: the location lookup waits for the full address */
  cityLoading: boolean;
  /** from the farm details: the farm list does not carry the country */
  country: COUNTRY_CODE | null;
  /** the year the map bar counts crops for */
  cropYear: number;
  fields: FieldListField[];
  fieldsLoading: boolean;
  fieldStats: HomeFieldStats;
  fieldStatsLoading: boolean;
  /** the field list failed to load: the map shows neither fields nor the "add your fields" prompt */
  fieldsUnavailable: boolean;
  /** the field practices failed to load: the crop count can't be shown */
  fieldStatsUnavailable: boolean;
  fieldStatusMap: Record<string, FieldStatus>;
  /** "cards": 3 cards beside the map, "two-cards": 2, "full": the map takes the row */
  heightVariant: "cards" | "two-cards" | "full";
  /** the farm's program has field management: fields can be added and the fields page opened */
  hasFieldManagement: boolean;
  onAddFields: () => void;
};

const FARM_ZOOM = 13;
const COUNTRY_ZOOM = 5;

// gestures as on the fields page; no keyboard shortcuts, a focused map would take the scrolling page's keys
const MAP_OPTIONS = (maps: typeof google.maps) => ({
  mapTypeId: maps.MapTypeId.HYBRID,
  disableDefaultUI: true,
  zoomControl: false,
  clickableIcons: false,
  gestureHandling: "cooperative",
  keyboardShortcuts: false,
  disableDoubleClickZoom: true,
});

type FarmMapPinProps = {
  /** read by google-map-react, which places the pin at this point and moves it with the map */
  lat: number;
  lng: number;
  label: string;
};

const FarmMapPin = ({ label }: FarmMapPinProps) => (
  <div className="farm-map-pin" data-testid="farm-map-pin">
    <span className="farm-map-pin-icon">
      <Icon iconName="klim/map-pin" size="s" />
    </span>
    <span className="farm-map-pin-label">{label}</span>
  </div>
);

export const FarmMapPanel = ({
  farm,
  city,
  cityLoading,
  country,
  cropYear,
  fields,
  fieldsLoading,
  fieldStats,
  fieldStatsLoading,
  fieldsUnavailable,
  fieldStatsUnavailable,
  fieldStatusMap,
  heightVariant,
  hasFieldManagement,
  onAddFields,
}: FarmMapPanelProps) => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const hasMapCookieConsent = useCookieConsent(checkExperienceCookieConsent);
  const hasNoFields = !fieldsLoading && !fieldsUnavailable && fields.length === 0;
  // the hook refetches every cached shape query when the count changes: no count until the fields are known, and
  // cached shapes that still match the fields are kept (the fields page refreshes them on every visit)
  const { fieldShapes, fieldShapesLoading } = useGetFieldShapes({
    enabled: fields.length > 0 && !fieldsUnavailable,
    fieldCount: fieldsLoading || fieldsUnavailable ? undefined : fields.length,
    refetchOnFirstCount: false,
  });
  const farmLocation = useFarmLocation({
    farmId: farm.id,
    zip: farm.zip,
    city,
    country,
    enabled: hasNoFields && hasMapCookieConsent && !cityLoading,
  });
  const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
  const [googleMaps, setGoogleMaps] = useState<typeof google.maps | null>(null);
  const centredOnRef = useRef<Coordinates | null>(null);

  const ready = !fieldsLoading && !fieldShapesLoading;
  // without fields the map opens on the farm, so it waits until the location lookup has settled
  const locationSettled = !hasNoFields || farmLocation.status === "ready" || farmLocation.status === "unavailable";
  const showMap = hasMapCookieConsent && ready && locationSettled;
  const showPin = hasNoFields && farmLocation.status === "ready";
  const farmPoint = farmLocation.location;
  const fallbackCenter = countryCentre(country);
  const defaultCenter = hasNoFields ? farmPoint ?? fallbackCenter : fields[0]?.location ?? fallbackCenter;
  const defaultZoom = (hasNoFields && !farmPoint) || fieldsUnavailable ? COUNTRY_ZOOM : FARM_ZOOM;

  const goToFields = useCallback(() => navigate(ALL_FIELDS_ROUTE), [navigate]);

  const handleGoogleApiLoaded = useCallback(({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
    setGoogleMap(map);
    setGoogleMaps(maps);
  }, []);

  useMapPolygons(googleMap, googleMaps, hasNoFields ? [] : fieldShapes, fieldStatusMap, hasFieldManagement ? goToFields : undefined);

  // the map opens on the geocoded point; this moves it there when the point arrives while the map is already open on the
  // country view, once per point, so that a re-render with the same point does not undo a drag
  useEffect(() => {
    if (!googleMap || !showPin || !farmPoint) return;
    const centredOn = centredOnRef.current;
    if (centredOn && centredOn.lat === farmPoint.lat && centredOn.lng === farmPoint.lng) return;
    centredOnRef.current = farmPoint;
    googleMap.setCenter(farmPoint);
    googleMap.setZoom(FARM_ZOOM);
  }, [googleMap, showPin, farmPoint]);

  // the pin lives in the map's own overlay layers, so the dimming of the imagery under it is a map layer too
  useEffect(() => {
    if (!googleMap || !googleMaps || !hasNoFields) return undefined;
    const dimLayer: google.maps.MapType = {
      name: "dim",
      alt: null,
      projection: null,
      radius: EARTH_RADIUS_M,
      minZoom: 0,
      maxZoom: MAP_LAYER_MAX_ZOOM,
      tileSize: new googleMaps.Size(GOOGLE_MAPS_TILE_SIZE_PX, GOOGLE_MAPS_TILE_SIZE_PX),
      getTile: (_tileCoord, _zoom, ownerDocument) => {
        const tile = (ownerDocument ?? document).createElement("div");
        tile.className = "farm-map-dim-tile";
        tile.style.width = `${GOOGLE_MAPS_TILE_SIZE_PX}px`;
        tile.style.height = `${GOOGLE_MAPS_TILE_SIZE_PX}px`;
        return tile;
      },
      releaseTile: () => undefined,
    };
    googleMap.overlayMapTypes.push(dimLayer);
    return () => {
      const index = googleMap.overlayMapTypes.getArray().indexOf(dimLayer);
      if (index >= 0) googleMap.overlayMapTypes.removeAt(index);
    };
  }, [googleMap, googleMaps, hasNoFields]);

  const zoomBy = (delta: number) => {
    if (!googleMap) return;
    googleMap.setZoom((googleMap.getZoom() ?? FARM_ZOOM) + delta);
  };

  return (
    <div className={`farm-map-panel ${heightVariant}`} data-testid="farm-map-panel">
      <div className="farm-map-google">
        {showMap ? (
          <GoogleMapReact
            bootstrapURLKeys={{ key: getGoogleKey() }}
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            options={MAP_OPTIONS}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleGoogleApiLoaded}
          >
            {showPin && farmPoint ? (
              <FarmMapPin lat={farmPoint.lat} lng={farmPoint.lng} label={city ? t("farmMap.pinLabel", { farmName: farm.name, city }) : farm.name} />
            ) : null}
          </GoogleMapReact>
        ) : null}
        {!hasMapCookieConsent ? (
          <LazyLoadImage className="farm-map-placeholder" wrapperClassName="farm-map-placeholder-wrapper" src={MAP_PLACEHOLDER_IMAGE} alt="" visibleByDefault />
        ) : null}
      </div>
      {!hasMapCookieConsent ? (
        <div className="farm-map-overlay">
          <MapCookieConsentReminder />
        </div>
      ) : null}
      {showMap ? <FarmMapZoomControl onZoomIn={() => zoomBy(1)} onZoomOut={() => zoomBy(-1)} /> : null}
      <div className="farm-map-bottom">
        {!ready ? (
          <div data-testid="farm-map-bottom-loading">
            <Skeleton type="block" height="4.5rem" />
          </div>
        ) : fieldsUnavailable ? (
          <FarmMapUnavailableBar />
        ) : hasNoFields ? (
          <FarmMapEmptyBar canAddFields={hasFieldManagement} onAddFields={onAddFields} />
        ) : (
          <FarmMapStatsBar
            fieldCount={fieldStats.fieldCount}
            totalHectares={fieldStats.totalHectares}
            cropCount={fieldStats.cropCount}
            cropCountLoading={fieldStatsLoading}
            cropCountUnavailable={fieldStatsUnavailable}
            cropYear={cropYear}
            onViewFields={hasFieldManagement ? goToFields : undefined}
          />
        )}
      </div>
    </div>
  );
};
